// ============================================================================
// ROL SHABLONLARI
//
// Restoran yaratilganda tanlangan restoran shabloniga ko'ra shu rollardan
// nusxa olinadi. Keyin ega ularni o'zgartirishi yoki yangi rol yaratishi mumkin.
// Ega roli qulflangan: unda doim barcha huquqlar bor.
// ============================================================================

import { PERMISSION_KEYS } from './permissions';
import type { PermissionKey } from './permissions';

/** Qurilma profillari: qaysi qurilmada qaysi ekranlar ochiladi */
export const DEVICE_KINDS = {
  pos: 'Kassa',
  kds: 'Oshxona ekrani',
  waiter: 'Ofitsiant qurilmasi',
  queue: 'Mijoz navbat ekrani',
  manager: 'Ega qurilmasi (telefon, kompyuter)',
} as const;

export type DeviceKind = keyof typeof DEVICE_KINDS;

export interface RoleTemplate {
  key: string;
  label: string;
  description: string;
  permissions: readonly PermissionKey[];
  /** Limitli huquqlar uchun standart qiymatlar */
  limits?: Partial<Record<PermissionKey, number>>;
  /** Xodim qaysi qurilma turlariga PIN bilan kira oladi */
  deviceKinds: readonly DeviceKind[];
  /** O'chirib yoki huquqini kamaytirib bo'lmaydi */
  locked?: boolean;
  /** PIN uzunligi: huquqi katta rollar uchun 6 */
  pinLength: 4 | 6;
}

const MANAGER_EXCLUDED: readonly PermissionKey[] = [
  'report.profit',
  'staff.roles',
  'devices.manage',
  'billing.view',
];

export const ROLE_TEMPLATES = [
  {
    key: 'owner',
    label: 'Ega',
    description: 'Restoran egasi. Barcha huquqlar, o‘zgartirib bo‘lmaydi.',
    permissions: PERMISSION_KEYS,
    deviceKinds: ['pos', 'kds', 'waiter', 'manager'],
    locked: true,
    pinLength: 6,
  },
  {
    key: 'manager',
    label: 'Menejer',
    description: 'Kundalik boshqaruv: menyu, xodimlar, sklad, bekor qilish. Foyda va obunani ko‘rmaydi.',
    permissions: PERMISSION_KEYS.filter((k) => !MANAGER_EXCLUDED.includes(k)),
    deviceKinds: ['pos', 'kds', 'waiter', 'manager'],
    pinLength: 6,
  },
  {
    key: 'cashier',
    label: 'Kassir',
    description: 'Savdo, to‘lov, smena. Chegirma 10% gacha.',
    permissions: [
      'pos.sell',
      'pos.take_payment',
      'pos.discount',
      'pos.reprint',
      'pos.hall',
      'pos.precheck',
      'order.history',
      'order.status',
      'shift.open_close',
      'expense.add',
      'meal.give',
      'menu.availability',
    ],
    limits: { 'pos.discount': 10 },
    deviceKinds: ['pos'],
    pinLength: 4,
  },
  {
    key: 'waiter',
    label: 'Ofitsiant',
    description: 'Stolga buyurtma, hisob cheki, to‘lov qabul qilish, o‘z savdosini ko‘rish.',
    permissions: ['pos.sell', 'pos.hall', 'pos.take_payment', 'pos.precheck', 'pos.my_sales'],
    deviceKinds: ['waiter', 'pos'],
    pinLength: 4,
  },
  {
    key: 'head_waiter',
    label: 'Katta ofitsiant',
    description: 'Zal menejeri: istalgan stol, stolni o‘tkazish, hisobni ochish, chegirma 10% gacha.',
    permissions: [
      'pos.sell',
      'pos.hall',
      'pos.take_payment',
      'pos.precheck',
      'pos.discount',
      'pos.other_tables',
      'pos.transfer_table',
      'pos.bill_unlock',
      'pos.my_sales',
      'order.history',
      'order.status',
      'menu.availability',
    ],
    limits: { 'pos.discount': 10 },
    deviceKinds: ['waiter', 'pos'],
    pinLength: 6,
  },
  {
    key: 'cook',
    label: 'Oshpaz',
    description: 'Oshxona ekrani, buyurtma holati, “tugadi” belgisi.',
    permissions: ['kds.use', 'order.status', 'menu.availability'],
    deviceKinds: ['kds'],
    pinLength: 4,
  },
  {
    key: 'barista',
    label: 'Barista',
    description: 'Bar ekrani va buyurtma holati.',
    permissions: ['kds.use', 'order.status', 'menu.availability'],
    deviceKinds: ['kds', 'pos'],
    pinLength: 4,
  },
  {
    key: 'bartender',
    label: 'Barmen',
    description: 'Bar: savdo, ochiq hisob, to‘lov, bar ekrani. Chegirma 10% gacha.',
    permissions: [
      'pos.sell',
      'pos.take_payment',
      'pos.discount',
      'pos.reprint',
      'pos.hall',
      'pos.precheck',
      'order.history',
      'order.status',
      'kds.use',
      'menu.availability',
    ],
    limits: { 'pos.discount': 10 },
    deviceKinds: ['pos', 'kds'],
    pinLength: 4,
  },
  {
    key: 'storekeeper',
    label: 'Omborchi',
    description: 'Sklad: kirim (narx bilan), inventarizatsiya, isrof.',
    permissions: ['stock.view', 'stock.receive', 'stock.receive_price', 'stock.adjust'],
    deviceKinds: ['pos', 'manager'],
    pinLength: 4,
  },
] as const satisfies readonly RoleTemplate[];

export type RoleTemplateKey = (typeof ROLE_TEMPLATES)[number]['key'];

const ROLE_MAP: ReadonlyMap<string, RoleTemplate> = new Map(
  ROLE_TEMPLATES.map((r) => [r.key, r as RoleTemplate] as const),
);

export function getRoleTemplate(key: string): RoleTemplate | undefined {
  return ROLE_MAP.get(key);
}
